import type { Emitter } from "@core/events/emitter";

/**
 * `ISketchModel` — web port of Unity
 * `Models/AnnotationsModel/ISketchModel.cs`.
 *
 * Owns the sketch IMAGE pipeline: the per-sketch blob-URL caches for the
 * three S3 PNG layers (strokes / background screenshot / blended preview),
 * their upload/download, and the editing-session flag. The sketch's
 * annotation ROW (create / `SketchUploaded` gate / cancel-delete) is owned by
 * `IAnnotationsModel` — mirroring Unity's SketchModel ⇄ AnnotationsLayerModel
 * split.
 *
 * Web deviations from the Unity contract (documented, deliberate):
 *   - Texture caches hold `blob:` URLs, not `Texture2D`s; callers draw them
 *     into `<img>` / canvas. `deleteCachedTextures` revokes the URLs.
 *   - Screenshot capture lives in the runtime layer (`SketchToolController`
 *     renders the Three.js scene) — models stay renderer-free, so
 *     `TakeInitialScreenshot` has no direct equivalent here; the controller
 *     captures and calls {@link saveBackground}.
 *   - No `SnapshotMode` state machine (no remote-avatar hiding to coordinate
 *     beyond the capture call itself).
 */
export interface ISketchModel {
  /** A layer changed locally (save background / flatten) — refresh previews. */
  readonly onScreenshotUpdated: Emitter<{ sketchId: string }>;
  /**
   * All uploads for a save completed (Unity `SketchUploaded`). The
   * registration layer forwards this to the `sketch_uploaded` (tc 36)
   * session event so peers re-download.
   */
  readonly onSketchUploaded: Emitter<{ sketchId: string }>;
  /** The sketch editor closed without saving (Unity `EditingCancelled`). */
  readonly onEditingCancelled: Emitter<{ sketchId: string }>;

  /** Non-null while the editor owns a sketch (Unity `IsEditing`/`m_editedSketchId`). */
  readonly editingSketchId: string | null;

  beginEdit(sketchId: string): void;
  endEdit(): void;
  /** Cancel the editing session (emits {@link onEditingCancelled}). */
  cancelSketch(): void;

  /**
   * Load one layer as a cached `blob:` URL (`null` when the object is
   * missing — e.g. legacy single-image sketches have no `-bg.png`).
   * Per-id in-flight dedup mirrors Unity's `AsyncNamedLock` (C3D-2800).
   */
  loadBackgroundTexture(sketchId: string): Promise<string | null>;
  loadSketchTexture(sketchId: string): Promise<string | null>;
  loadSketchPreviewTexture(sketchId: string): Promise<string | null>;

  /**
   * Cache + upload the capture screenshot as `Sketchs/{id}-bg.png`
   * (Unity `SaveBackground`, fired right after `TakeInitialScreenshot`).
   */
  saveBackground(sketchId: string, pngBytes: Uint8Array): Promise<void>;

  /**
   * Cache + upload the flattened stroke layer and the blended preview
   * (Unity `UpdateScreenshot` → `SaveSketch`): emits
   * {@link onScreenshotUpdated} immediately (the editor closes on it, uploads
   * continue in the background), uploads `-preview.png` then `.png`, then
   * emits {@link onSketchUploaded}.
   */
  updateScreenshot(
    sketchId: string,
    strokesPng: Uint8Array,
    previewPng: Uint8Array,
  ): Promise<void>;

  /**
   * Drop (and revoke) the cached layer URLs for one sketch — called when its
   * annotation is destroyed and when a remote `sketch_uploaded` invalidates
   * the local copies (Unity `DeleteCachedTextures`).
   */
  deleteCachedTextures(sketchId: string): void;

  /** Remote `sketch_uploaded` received: purge caches + re-emit for renderers. */
  applyRemoteSketchUploaded(sketchId: string): void;
}

export const SKETCH_MODEL = Symbol.for("Campfire.SketchModel");
